import { fetchRuntimeConfig } from '@accenturecodefoundry/mfe-pot-shared-runtime-config';
import { initBrowserObservability } from '@accenturecodefoundry/mfe-pot-shared-observability';

/**
 * Replaces environment.ts/environment.prod.ts + fileReplacements -- see
 * CLAUDE.md's Hosting section. Under plain `nx serve` these dev defaults
 * apply directly; in a container, the entrypoint script injects real values
 * via window.__mfePotEnv from the Helm chart's ConfigMap.
 *
 * Fetched from this app's own origin (not read off window.__mfePotEnv)
 * because this app is loaded as a Native Federation remote inside the
 * shell as often as it's loaded standalone -- when federated, this app's
 * own index.html/env.js never loads (the shell's already-running page
 * just imports this app's JS module into the same window), so
 * window.__mfePotEnv only ever carries the shell's own values. Fetching
 * this app's own env.js directly from its own origin works in both cases.
 *
 * This app previously had no runtime-config surface at all (no BFF of its
 * own) -- added now specifically because ContentClient needs a
 * strapiBaseUrl, the same way dashboard's does.
 */
const devDefaults = {
  strapiBaseUrl: 'http://localhost:1337',
  // No local FES for plain `nx serve` -- provider mounts but fetch fails, leaving
  // status: 'degraded' (no province badge, no FES name). In a container the Helm
  // chart's ConfigMap injects the real URL via window.__mfePotEnv.
  citizenContextBaseUrl: 'http://localhost:3006',
  // job-bank-bff's own origin -- NOT same-origin like job-bank-mfe's own ""
  // default, since this app is a different remote calling into job-bank's
  // BFF across hostnames (see check-bff-boundaries.mjs's CROSS_DOMAIN_EXCEPTIONS
  // entry for life-events-mfe). Bare origin, no /api suffix -- the fetch call
  // appends /api/v1/recommendations itself (job-bank-bff's versioned route --
  // see CLAUDE.md's "Permitted BFF/service call targets" section).
  jobBankBffBaseUrl: 'http://localhost:3001',
  // undefined -- no local collector needed for plain `nx serve`, see
  // shared-observability's initBrowserObservability.
  otelExporterOtlpEndpoint: undefined as string | undefined,
};

export async function loadRuntimeConfig(ownOriginUrl: string) {
  const config = await fetchRuntimeConfig(ownOriginUrl, devDefaults);
  // Single wiring point for this app's one exposed entry point (App.tsx).
  // Idempotent, so a second call is safe. propagateTraceHeaderCorsUrls now
  // matches job-bank-bff's Ingress origin (exposed under job-bank-mfe's own
  // hostname, not this app's) -- required so the job-matches fetch's
  // traceparent header actually crosses this cross-origin call, not just
  // this app's own (nonexistent) BFF -- see
  // mfe-pot-platform/CLAUDE.md's observability section.
  initBrowserObservability({
    serviceName: 'life-events-mfe',
    otlpEndpoint: config.otelExporterOtlpEndpoint,
    propagateTraceHeaderCorsUrls: [/^https?:\/\/job-bank-mfe\./],
  });
  return config;
}
